// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';

import './List.less';

/**
 * `List` component.
 */
class List extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sortColumn: this.props.sortColumn,
            sortDirection: this.props.sortDirection,
            selection: this.props.selection,
            headerChecked: false,
        };

        this.handleRowSelectionChange = this.handleRowSelectionChange.bind(this);
        this.handleGlobalSelectionChange = this.handleGlobalSelectionChange.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const { sortColumn, sortDirection, selection } = nextProps;
        if (this.state.sortColumn !== sortColumn || this.state.sortDirection !== sortDirection) {
            this.setState({ sortColumn, sortDirection });
        }

        if (selection && this.state.selection !== selection) {
            this.setState({
                selection,
                headerChecked: selection.length === this.props.data.filter(({ disabled }) => !disabled).length,
            });
        }
    }

    getData() {
        const { sortColumn, sortDirection } = this.state;
        const [column] = this.props.columns.filter(({ key }) => key === sortColumn);
        const defaultSort = (a, b) => {
            if (a > b) {
                return 1;
            }
            if (a < b) {
                return -1;
            }
            return 0;
        };

        if (!column) {
            return this.props.data;
        }
        return this.props.data.sort((a, b) => (column.sort ? column.sort(a, b) : defaultSort(a[sortColumn], b[sortColumn])) * (sortDirection === 'ASC' ? 1 : -1));
    }

    sort(columnKey) {
        let { sortColumn, sortDirection } = this.state;
        sortDirection = sortColumn === columnKey && sortDirection === 'ASC' ? 'DESC' : 'ASC';
        sortColumn = columnKey;

        this.setState({ sortColumn, sortDirection });
        if (this.props.onSortChange) {
            this.props.onSortChange({ sortColumn, sortDirection });
        }
    }

    isRowSelected(row) {
        return this.state.selection.indexOf(this.getRowKey(row)) !== -1;
    }

    handleRowSelectionChange(event) {
        this.setState({ headerChecked: false });

        let selection;
        if (event.target.checked) {
            selection = this.state.selection.slice();
            selection.push(event.target.value);
        } else {
            selection = this.state.selection.filter(value => value !== event.target.value);
        }
        this.setSelection(selection);
    }

    handleGlobalSelectionChange(event) {
        const headerChecked = event.target.checked;
        this.setState({ headerChecked });

        const selection = headerChecked ? this.props.data.filter(({ disabled }) => !disabled).map(row => this.getRowKey(row)) : [];
        this.setSelection(selection);
    }

    setSelection(selection) {
        this.setState({ selection });
        if (this.props.onSelectionChange) {
            this.props.onSelectionChange(selection.slice());
        }
    }

    getRowKey(row) {
        const { rowKey } = this.props;
        if (typeof rowKey === 'function') {
            return rowKey(row);
        }

        return row[rowKey];
    }

    render() {
        const {
            baseClassName,
            className,
            data,
            columns,
            sortColumn,
            sortDirection,
            onSortChange,
            selection,
            onSelectionChange,
            rowKey,
            ...props
        } = this.props;

        const realColumn = [...columns];
        if (onSelectionChange) {
            realColumn.unshift({
                key: 'selection',
                className: 'cell-shrink',
                title: (
                    <input
                        type="checkbox"
                        className="checkbox"
                        checked={this.state.headerChecked}
                        onChange={this.handleGlobalSelectionChange}
                    />
                ),
                render: row => (
                    <input
                        type="checkbox"
                        className="checkbox"
                        value={this.getRowKey(row)}
                        checked={this.isRowSelected(row)}
                        disabled={row.disabled}
                        onChange={this.handleRowSelectionChange}
                    />
                ),
            });
        }

        return (
            <table className={classNames(baseClassName, className)} {...props}>
                <thead>
                    <tr>
                        {realColumn.map(({ key, title, sortable, render, ...headerProps }) => {
                            if (sortable) {
                                title = <div className="th__action">{title}</div>;
                                headerProps.className = classNames({
                                    'is-sortable': sortable,
                                    'is-sorted is-sorted--up': this.state.sortColumn === key && this.state.sortDirection === 'ASC',
                                    'is-sorted is-sorted--down': this.state.sortColumn === key && this.state.sortDirection === 'DESC',
                                }, headerProps.className);
                                headerProps.onClick = () => this.sort(key);
                            }

                            return (
                                <th key={key} {...headerProps}>{title}</th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {this.getData().map(row => (
                        <tr key={this.getRowKey(row)} className={this.isRowSelected(row) ? 'is-selected' : null}>
                            {realColumn.map(({ key, render }) => (
                                <td key={key}>
                                    {render ? render(row) : row[key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }
}
List.propTypes = {
    /**
     * Columns definition.
     *
     * In addition to the properties described below, you can use any native properties
     * supported by the `TH` element like `width`, `className`, `style`, etc.
     */
    columns: PropTypes.arrayOf(PropTypes.shape({
        /**
         * Unique column key.
         */
        key: PropTypes.string.isRequired,
        /**
         * Column title.
         */
        title: PropTypes.node,
        /**
         * Optional cell renderer function: `row => Component`. By default render value of cell.
         */
        render: PropTypes.func,
        /**
         * Is column sortable.
         * Default is `false`
         */
        sortable: PropTypes.bool,
        /**
         * Optional function for row comparison: `(row1, row2) => -1 | 0 | 1`
         */
        sort: PropTypes.func,
    })).isRequired,
    /**
     * Array of row objects.
     *
     * Each row must contains `key` property, all columns keys, and can contain `disabled` property.
     */
    data: PropTypes.arrayOf(PropTypes.shape({
        /**
         * Unique column key. This property can by renamed using `rowKey` property of List.
         */
        key: PropTypes.string,
        /**
         * Optional property for disabling ability to select this row.
         */
        disabled: PropTypes.bool,
    })),
    /**
     * Current sort column key
     */
    sortColumn: PropTypes.string,
    /**
     * Sort direction of the column being sorted.
     */
    sortDirection: PropTypes.oneOf(['ASC', 'DESC']),
    /**
     * Sort change handler
     */
    onSortChange: PropTypes.func,
    /**
     * Array of keys of selected rows
     */
    selection: PropTypes.array,
    /**
     * Selection change handler for list. Checkboxes will only be rendered if this prop is defined.
     */
    onSelectionChange: PropTypes.func,
    /**
     * Row's key getter. Could be a string or function like: `row => <unique_string>`
     */
    rowKey: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
    ]),
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
List.defaultProps = {
    data: [],
    sortColumn: null,
    sortDirection: 'ASC',
    onSortChange: null,
    onSelectionChange: null,
    selection: [],
    rowKey: 'key',
    className: null,
    baseClassName: `${CLS_PREFIX}list`,
};

export default List;



// WEBPACK FOOTER //
// ./components/List/List.js